import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Power, Clock, Volume2 } from 'lucide-react';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SettingsHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
`;

const SettingsSection = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &.highlight-blink {
    animation: blinkHighlight 3s ease-in-out forwards;
  }
  
  @keyframes blinkHighlight {
    0% {
      border-color: ${({ theme }) => theme.colors.dark.border};
      box-shadow: none;
    }
    8.33% {
      border-color: ${({ theme }) => theme.colors.primary[500]};
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.primary[500]}40;
    }
    16.66% {
      border-color: ${({ theme }) => theme.colors.dark.border};
      box-shadow: none;
    }
    25% {
      border-color: ${({ theme }) => theme.colors.primary[500]};
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.primary[500]}40;
    }
    33.33% {
      border-color: ${({ theme }) => theme.colors.dark.border};
      box-shadow: none;
    }
    41.66% {
      border-color: ${({ theme }) => theme.colors.primary[500]};
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.primary[500]}40;
    }
    50%, 100% {
      border-color: ${({ theme }) => theme.colors.dark.border};
      box-shadow: none;
    }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionIcon = styled.div`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SettingLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const SettingName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark.text};
`;

const SettingDescription = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.colors.gray[600]};
    transition: 0.3s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: ${({ theme }) => theme.colors.primary[500]};
  }
  
  input:checked + span:before {
    transform: translateX(24px);
  }
`;

const TimeRangeContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const TimeInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

const AppSettings = ({ onSettingsChange, highlightBackgroundSettings, highlightActiveHours }) => {
  const [settings, setSettings] = useState({
    backgroundListening: true,
    activeHours: false,
    startTime: '09:00',
    endTime: '18:00',
    autoStart: true,
    notifications: true,
    voiceActivation: true
  });
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isActiveHoursHighlighted, setIsActiveHoursHighlighted] = useState(false);

  useEffect(() => {
    // Load settings from localStorage
    const savedBackgroundListening = localStorage.getItem('backgroundListening');
    const savedActiveHours = localStorage.getItem('activeHours');
    const savedStartTime = localStorage.getItem('startTime');
    const savedEndTime = localStorage.getItem('endTime');
    
    setSettings(prev => ({
      ...prev,
      backgroundListening: savedBackgroundListening !== null ? JSON.parse(savedBackgroundListening) : true,
      activeHours: savedActiveHours !== null ? JSON.parse(savedActiveHours) : false,
      startTime: savedStartTime || '09:00',
      endTime: savedEndTime || '18:00'
    }));

    // Load auto-start status from OS (source of truth)
    const loadAutoStartStatus = async () => {
      if (window.electronAPI?.getAutoStart) {
        try {
          const enabled = await window.electronAPI.getAutoStart();
          setSettings(prev => ({ ...prev, autoStart: enabled }));
        } catch (error) {
          console.error('Failed to get auto-start status:', error);
        }
      }
    };
    loadAutoStartStatus();
  }, []);

  useEffect(() => {
    if (highlightBackgroundSettings) {
      setIsHighlighted(true);
      // Auto-remove highlight after animation completes (3 seconds)
      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightBackgroundSettings]);

  useEffect(() => {
    if (highlightActiveHours) {
      setIsActiveHoursHighlighted(true);
      // Auto-remove highlight after animation completes (3 seconds)
      const timer = setTimeout(() => {
        setIsActiveHoursHighlighted(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightActiveHours]);

  const handleToggle = async (setting) => {
    const newValue = !settings[setting];
    
    // Handle auto-start specially (uses Electron API, not localStorage)
    if (setting === 'autoStart') {
      if (window.electronAPI?.setAutoStart) {
        try {
          const result = await window.electronAPI.setAutoStart(newValue);
          if (result.success) {
            setSettings(prev => ({ ...prev, autoStart: newValue }));
          }
        } catch (error) {
          console.error('Failed to set auto-start:', error);
          return; // Don't update UI if failed
        }
      }
    } else {
      // Other settings use localStorage
      setSettings(prev => ({
        ...prev,
        [setting]: newValue
      }));
      
      // Save to localStorage
      localStorage.setItem(setting, JSON.stringify(newValue));
      
      // Trigger window event to sync with Dashboard
      window.dispatchEvent(new CustomEvent('settingsChanged', {
        detail: { [setting]: newValue }
      }));
    }
    
    if (onSettingsChange) {
      onSettingsChange(true);
    }
  };

  const handleTimeChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Save to localStorage
    localStorage.setItem(field, value);
    
    if (onSettingsChange) {
      onSettingsChange(true);
    }
  };

  return (
    <SettingsContainer>
      <SettingsHeader>
        <Title>App Settings</Title>
        <Description>Configure your MyAI Assistant preferences</Description>
      </SettingsHeader>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={isHighlighted ? 'highlight-blink' : ''}
      >
        <SectionHeader>
          <SectionIcon><Power /></SectionIcon>
          <SectionTitle>Background Processing</SectionTitle>
        </SectionHeader>

        <SettingRow>
          <SettingLabel>
            <SettingName>Enable Background Listening</SettingName>
            <SettingDescription>
              Allow SARVIK to listen and process conversations in the background
            </SettingDescription>
          </SettingLabel>
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={settings.backgroundListening}
              onChange={() => handleToggle('backgroundListening')}
            />
            <span></span>
          </ToggleSwitch>
        </SettingRow>

        <SettingRow>
          <SettingLabel>
            <SettingName>Auto-start with System</SettingName>
            <SettingDescription>
              Launch SARVIK automatically when you start your computer
            </SettingDescription>
          </SettingLabel>
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={settings.autoStart}
              onChange={() => handleToggle('autoStart')}
            />
            <span></span>
          </ToggleSwitch>
        </SettingRow>
      </SettingsSection>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className={isActiveHoursHighlighted ? 'highlight-blink' : ''}
      >
        <SectionHeader>
          <SectionIcon><Clock /></SectionIcon>
          <SectionTitle>Active Hours</SectionTitle>
        </SectionHeader>

        <SettingRow>
          <SettingLabel>
            <SettingName>Set Active Hours</SettingName>
            <SettingDescription>
              Only process conversations during specific hours
            </SettingDescription>
          </SettingLabel>
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={settings.activeHours}
              onChange={() => handleToggle('activeHours')}
            />
            <span></span>
          </ToggleSwitch>
        </SettingRow>

        {settings.activeHours && (
          <TimeRangeContainer>
            <TimeInput
              type="time"
              value={settings.startTime}
              onChange={(e) => handleTimeChange('startTime', e.target.value)}
            />
            <span style={{ color: '#6b7280' }}>to</span>
            <TimeInput
              type="time"
              value={settings.endTime}
              onChange={(e) => handleTimeChange('endTime', e.target.value)}
            />
          </TimeRangeContainer>
        )}
      </SettingsSection>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <SectionHeader>
          <SectionIcon><Volume2 /></SectionIcon>
          <SectionTitle>Voice & Notifications</SectionTitle>
        </SectionHeader>

        <SettingRow>
          <SettingLabel>
            <SettingName>Voice Activation</SettingName>
            <SettingDescription>
              Enable wake word detection ("Hey SARVIK")
            </SettingDescription>
          </SettingLabel>
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={settings.voiceActivation}
              onChange={() => handleToggle('voiceActivation')}
            />
            <span></span>
          </ToggleSwitch>
        </SettingRow>

        <SettingRow>
          <SettingLabel>
            <SettingName>Enable Notifications</SettingName>
            <SettingDescription>
              Receive notifications for reminders and important updates
            </SettingDescription>
          </SettingLabel>
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={settings.notifications}
              onChange={() => handleToggle('notifications')}
            />
            <span></span>
          </ToggleSwitch>
        </SettingRow>
      </SettingsSection>
    </SettingsContainer>
  );
};

export default AppSettings;
